/*
 * Active Route View (driver "Start Route" screen)
 *
 * Full-screen live map the driver sees after starting a route:
 * - The assigned route polyline and its stops
 * - The truck's live GPS position (WebSocket updates + polling fallback)
 * - ON ROUTE / OFF ROUTE indicator based on distance to the route line
 * - Route progress percentage and next stop information
 * - End Route button to finish the session
 */

import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { MapContainer, TileLayer, Polyline, Marker, Popup, useMap } from 'react-leaflet';
import axios from 'axios';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { API, WS_URL } from '../../utils/config';

// Distance from the route line (meters) beyond which the driver is "off route"
const OFF_ROUTE_THRESHOLD_M = 100;

// ---------------------------------------------------------------------------
// Geometry helpers
// ---------------------------------------------------------------------------

const toRad = (deg) => (deg * Math.PI) / 180;

// Haversine distance in meters
const distanceMeters = (a, b) => {
  const R = 6371000;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s));
};

// Project point p onto segment [a, b] using a local flat-earth approximation.
// Returns { point, t } where t is the fraction along the segment.
const projectOnSegment = (p, a, b) => {
  const latScale = Math.cos(toRad(a.lat));
  const ax = a.lng * latScale, ay = a.lat;
  const bx = b.lng * latScale, by = b.lat;
  const px = p.lng * latScale, py = p.lat;

  const dx = bx - ax, dy = by - ay;
  const lengthSq = dx * dx + dy * dy;
  let t = 0;
  if (lengthSq > 0) {
    t = ((px - ax) * dx + (py - ay) * dy) / lengthSq;
    t = Math.max(0, Math.min(1, t));
  }
  return {
    point: { lat: a.lat + t * (b.lat - a.lat), lng: a.lng + t * (b.lng - a.lng) },
    t
  };
};

// Find the closest point on the route polyline to the given position.
// Returns { distance (m), progressFraction (0..1), snapped point }.
const locateOnRoute = (position, coordinates, cumulative, totalLength) => {
  if (!coordinates || coordinates.length < 2 || !position) return null;

  let best = null;
  for (let i = 0; i < coordinates.length - 1; i++) {
    const { point, t } = projectOnSegment(position, coordinates[i], coordinates[i + 1]);
    const dist = distanceMeters(position, point);
    if (!best || dist < best.distance) {
      const segLen = cumulative[i + 1] - cumulative[i];
      best = {
        distance: dist,
        alongRoute: cumulative[i] + t * segLen,
        snapped: point
      };
    }
  }
  if (!best) return null;
  return {
    distance: best.distance,
    progressFraction: totalLength > 0 ? best.alongRoute / totalLength : 0,
    snapped: best.snapped
  };
};

// ---------------------------------------------------------------------------
// Map helpers
// ---------------------------------------------------------------------------

const FollowTruck = ({ position, follow }) => {
  const map = useMap();
  useEffect(() => {
    if (follow && position) {
      map.setView([position.lat, position.lng], map.getZoom(), { animate: true });
    }
  }, [position, follow, map]);
  return null;
};

const FitRouteOnce = ({ coordinates }) => {
  const map = useMap();
  const fitted = useRef(false);
  useEffect(() => {
    if (!fitted.current && coordinates && coordinates.length > 1) {
      map.fitBounds(L.latLngBounds(coordinates.map(c => [c.lat, c.lng])), { padding: [40, 40] });
      fitted.current = true;
    }
  }, [coordinates, map]);
  return null;
};

const truckIcon = (isOffRoute) => L.divIcon({
  html: `<div style="
    background: ${isOffRoute ? '#FEE2E2' : '#DCFCE7'};
    border: 3px solid ${isOffRoute ? '#EF4444' : '#16A34A'};
    border-radius: 50%;
    width: 44px;
    height: 44px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 22px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.4);
  ">🚛</div>`,
  className: '',
  iconSize: [44, 44],
  iconAnchor: [22, 22]
});

const stopIcon = (sequence, color) => L.divIcon({
  html: `<div style="
    background: white;
    border: 3px solid ${color};
    border-radius: 50%;
    width: 26px;
    height: 26px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: bold;
    font-size: 12px;
    color: ${color};
    box-shadow: 0 1px 4px rgba(0,0,0,0.3);
  ">${sequence}</div>`,
  className: '',
  iconSize: [26, 26],
  iconAnchor: [13, 13]
});

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const ActiveRouteView = ({ driver, vehicle, route, session, onEndRoute }) => {
  const [position, setPosition] = useState(() => {
    const loc = vehicle.last_location;
    return loc ? { lat: loc.latitude, lng: loc.longitude } : null;
  });
  const [speed, setSpeed] = useState(vehicle.last_location?.speed || 0);
  const [lastUpdate, setLastUpdate] = useState(vehicle.last_updated || null);
  const [wsConnected, setWsConnected] = useState(false);
  const [follow, setFollow] = useState(true);
  const [maxProgress, setMaxProgress] = useState(0);
  const [ending, setEnding] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const wsRef = useRef(null);

  // Precompute cumulative distances along the route
  const { cumulative, totalLength } = useMemo(() => {
    const coords = route.coordinates || [];
    const cum = [0];
    for (let i = 1; i < coords.length; i++) {
      cum.push(cum[i - 1] + distanceMeters(coords[i - 1], coords[i]));
    }
    return { cumulative: cum, totalLength: cum[cum.length - 1] || 0 };
  }, [route.coordinates]);

  // Where is the truck relative to the route?
  const routeStatus = useMemo(
    () => locateOnRoute(position, route.coordinates, cumulative, totalLength),
    [position, route.coordinates, cumulative, totalLength]
  );

  const isOffRoute = routeStatus ? routeStatus.distance > OFF_ROUTE_THRESHOLD_M : false;
  const progressPct = Math.round(maxProgress * 100);

  // Keep progress monotonic - reaching 100% means route completed
  useEffect(() => {
    if (routeStatus && !isOffRoute && routeStatus.progressFraction > maxProgress) {
      setMaxProgress(routeStatus.progressFraction);
    }
  }, [routeStatus, isOffRoute, maxProgress]);

  // Next stop: first stop (by position along the route) still ahead of the truck
  const stopsWithProgress = useMemo(() => {
    return (route.stops || [])
      .filter(s => s.is_active !== false)
      .map(s => {
        const loc = locateOnRoute(s.position, route.coordinates, cumulative, totalLength);
        return { ...s, routeFraction: loc ? loc.progressFraction : 1 };
      })
      .sort((a, b) => a.routeFraction - b.routeFraction);
  }, [route.stops, route.coordinates, cumulative, totalLength]);

  const nextStop = useMemo(() => {
    if (!routeStatus) return null;
    return stopsWithProgress.find(s => s.routeFraction >= routeStatus.progressFraction - 0.005) || null;
  }, [stopsWithProgress, routeStatus]);

  // Apply an incoming position update
  const applyLocation = useCallback((lat, lng, spd, ts) => {
    setPosition({ lat, lng });
    if (spd !== undefined && spd !== null) setSpeed(spd);
    if (ts) setLastUpdate(ts);
  }, []);

  // WebSocket live updates
  useEffect(() => {
    let closed = false;
    let reconnectTimer = null;
    let pingTimer = null;

    const connect = () => {
      try {
        const ws = new WebSocket(`${WS_URL}/ws/driver-${driver.id}`);
        wsRef.current = ws;

        ws.onopen = () => {
          setWsConnected(true);
          pingTimer = setInterval(() => {
            if (ws.readyState === WebSocket.OPEN) {
              ws.send(JSON.stringify({ type: 'ping' }));
            }
          }, 30000);
        };

        ws.onmessage = (event) => {
          try {
            const msg = JSON.parse(event.data);
            if (msg.type === 'location_update' && msg.vehicle_id === vehicle.id && msg.location) {
              applyLocation(
                msg.location.latitude,
                msg.location.longitude,
                msg.location.speed,
                msg.timestamp
              );
            }
          } catch (e) {
            // Ignore malformed messages
          }
        };

        ws.onclose = () => {
          setWsConnected(false);
          if (pingTimer) clearInterval(pingTimer);
          if (!closed) {
            reconnectTimer = setTimeout(connect, 5000);
          }
        };

        ws.onerror = () => {
          ws.close();
        };
      } catch (e) {
        reconnectTimer = setTimeout(connect, 5000);
      }
    };

    connect();
    return () => {
      closed = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (pingTimer) clearInterval(pingTimer);
      if (wsRef.current) wsRef.current.close();
    };
  }, [driver.id, vehicle.id, applyLocation]);

  // Polling fallback every 20s (in case WebSocket misses updates)
  useEffect(() => {
    const poll = async () => {
      try {
        const response = await axios.get(`${API}/driver/vehicle`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('driverToken')}` }
        });
        const v = response.data.vehicle;
        if (v?.last_location) {
          applyLocation(
            v.last_location.latitude,
            v.last_location.longitude,
            v.last_location.speed,
            v.last_updated
          );
        }
      } catch (e) {
        // Keep last known position on poll failure
      }
    };
    const timer = setInterval(poll, 20000);
    return () => clearInterval(timer);
  }, [applyLocation]);

  const handleEndRoute = async () => {
    if (!window.confirm('End this route?')) return;
    setEnding(true);
    await onEndRoute();
  };

  const lastUpdateText = useMemo(() => {
    if (!lastUpdate) return 'No GPS data yet';
    const ts = typeof lastUpdate === 'string' && !lastUpdate.endsWith('Z') && !lastUpdate.includes('+')
      ? `${lastUpdate}Z`
      : lastUpdate;
    const seconds = Math.max(0, Math.round((Date.now() - new Date(ts).getTime()) / 1000));
    if (seconds < 60) return `${seconds}s ago`;
    return `${Math.round(seconds / 60)} min ago`;
  }, [lastUpdate, position]);

  const routeColor = route.color || '#3B82F6';

  return (
    <div className="h-screen flex flex-col bg-gray-100">
      {/* Top status bar */}
      <div className={`px-4 py-3 text-white shadow-lg z-[1000] ${isOffRoute ? 'bg-red-600' : 'bg-green-700'}`}>
        <div className="flex items-center justify-between max-w-lg mx-auto">
          <div className="min-w-0">
            <p className="font-bold truncate">{route.name}</p>
            <p className="text-xs opacity-90">
              {position
                ? (isOffRoute
                    ? `⚠️ OFF ROUTE — ${Math.round(routeStatus.distance)} m from route`
                    : '✅ ON ROUTE')
                : '📡 Waiting for GPS...'}
            </p>
          </div>
          <button
            onClick={handleEndRoute}
            disabled={ending}
            className="ml-3 px-4 py-2 bg-white text-gray-900 rounded-lg font-semibold text-sm whitespace-nowrap disabled:opacity-50"
          >
            {ending ? 'Ending...' : '⏹ End Route'}
          </button>
        </div>

        {/* Progress bar */}
        <div className="max-w-lg mx-auto mt-2">
          <div className="flex justify-between text-[11px] opacity-90 mb-1">
            <span>Progress: {progressPct}%</span>
            <span>{speed ? `${Math.round(speed)} km/h` : ''} • GPS {lastUpdateText}</span>
          </div>
          <div className="w-full bg-black bg-opacity-25 rounded-full h-2">
            <div
              className="bg-white h-2 rounded-full transition-all duration-500"
              style={{ width: `${progressPct}%` }}
            ></div>
          </div>
        </div>
      </div>

      {/* Map */}
      <div className="flex-1 relative">
        <MapContainer
          center={position ? [position.lat, position.lng] : [17.2518, -88.7713]}
          zoom={15}
          style={{ height: '100%', width: '100%' }}
          zoomControl={false}
        >
          <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          <FitRouteOnce coordinates={route.coordinates} />
          <FollowTruck position={position} follow={follow} />

          {/* Route polyline */}
          <Polyline
            positions={(route.coordinates || []).map(c => [c.lat, c.lng])}
            color={routeColor}
            weight={6}
            opacity={0.8}
          />

          {/* Stops */}
          {(route.stops || []).filter(s => s.is_active !== false).map((stop) => (
            <Marker
              key={stop.id}
              position={[stop.position.lat, stop.position.lng]}
              icon={stopIcon(stop.sequence, routeColor)}
            >
              <Popup>
                <div className="text-sm">
                  <p className="font-bold">{stop.name}</p>
                  {stop.description && <p className="text-gray-600 text-xs">{stop.description}</p>}
                </div>
              </Popup>
            </Marker>
          ))}

          {/* Truck live position */}
          {position && (
            <Marker position={[position.lat, position.lng]} icon={truckIcon(isOffRoute)}>
              <Popup>
                <div className="text-sm">
                  <p className="font-bold">🚛 {vehicle.license_plate}</p>
                  <p className="text-xs text-gray-600">Updated {lastUpdateText}</p>
                </div>
              </Popup>
            </Marker>
          )}
        </MapContainer>

        {/* Help toggle */}
        <button
          onClick={() => setShowHelp(!showHelp)}
          className={`absolute bottom-40 right-4 z-[1000] w-12 h-12 rounded-full shadow-lg flex items-center justify-center text-xl font-bold ${
            showHelp ? 'bg-indigo-600 text-white' : 'bg-white text-indigo-600'
          }`}
          title="What does this screen mean?"
        >
          ?
        </button>

        {/* Help / legend card */}
        {showHelp && (
          <div className="absolute inset-x-4 top-4 z-[1001] bg-white rounded-xl shadow-2xl p-4 max-w-lg mx-auto text-sm text-gray-700 space-y-2 max-h-[70%] overflow-y-auto">
            <div className="flex justify-between items-center">
              <p className="font-bold text-gray-900">❓ What am I looking at?</p>
              <button onClick={() => setShowHelp(false)} className="text-gray-400 hover:text-gray-600 px-2">✕</button>
            </div>
            <ul className="space-y-2">
              <li><span className="font-semibold">Colored line</span> — the route you must drive, from start to end. Numbered circles are collection stops.</li>
              <li><span className="font-semibold">🚛 marker</span> — your truck's live GPS position. Green ring = on the route, red ring = off the route.</li>
              <li><span className="font-semibold text-green-700">Green bar ✅ ON ROUTE</span> — you are following the route correctly.</li>
              <li><span className="font-semibold text-red-700">Red bar ⚠️ OFF ROUTE</span> — you are more than 100 m from the route; it shows how far. Return to the route.</li>
              <li><span className="font-semibold">White progress bar</span> — how much of the route you have completed. 100% = route done.</li>
              <li><span className="font-semibold">Next stop</span> (bottom) — the upcoming stop and your distance to it.</li>
              <li><span className="font-semibold">🎯 button</span> — keeps the map centered on your truck. Tap it off to look around the map freely, tap again to re-follow.</li>
              <li><span className="font-semibold">● Live / ○ Reconnecting</span> — live GPS connection status. "GPS ... ago" shows how old the last position is.</li>
              <li><span className="font-semibold">⏹ End Route</span> — press when you finish (or must stop). Your times are recorded for the office.</li>
            </ul>
          </div>
        )}

        {/* Follow toggle */}
        <button
          onClick={() => setFollow(!follow)}
          className={`absolute bottom-24 right-4 z-[1000] w-12 h-12 rounded-full shadow-lg flex items-center justify-center text-xl ${
            follow ? 'bg-green-600 text-white' : 'bg-white text-gray-600'
          }`}
          title={follow ? 'Following truck (tap to stop)' : 'Tap to follow truck'}
        >
          🎯
        </button>

        {/* Connection indicator */}
        <div className="absolute top-2 right-2 z-[1000]">
          <span className={`text-[10px] px-2 py-1 rounded-full shadow ${
            wsConnected ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
          }`}>
            {wsConnected ? '● Live' : '○ Reconnecting'}
          </span>
        </div>
      </div>

      {/* Bottom info panel */}
      <div className="bg-white border-t border-gray-200 px-4 py-3 z-[1000]">
        <div className="max-w-lg mx-auto flex items-center justify-between">
          <div className="min-w-0">
            {nextStop ? (
              <>
                <p className="text-xs text-gray-500">Next stop</p>
                <p className="font-semibold text-gray-900 truncate">
                  {nextStop.sequence}. {nextStop.name}
                  {position && (
                    <span className="text-sm font-normal text-gray-500 ml-2">
                      {(() => {
                        const d = distanceMeters(position, nextStop.position);
                        return d >= 1000 ? `${(d / 1000).toFixed(1)} km` : `${Math.round(d)} m`;
                      })()}
                    </span>
                  )}
                </p>
              </>
            ) : (
              <p className="text-sm text-gray-600">
                {progressPct >= 99 ? '🏁 Route complete! You can end the route.' : `Truck: ${vehicle.license_plate}`}
              </p>
            )}
          </div>
          <div className="text-right ml-3">
            <p className="text-xs text-gray-500">Route length</p>
            <p className="font-semibold text-gray-900">{(totalLength / 1000).toFixed(1)} km</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ActiveRouteView;
